import { useState } from "react";
import {
  Box,Drawer,List,ListItemButton,ListItemIcon,ListItemText,
  Collapse,Typography,Tooltip
} from "@mui/material";
import { useLocation,useNavigate } from "react-router-dom";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import logo from "../../../static/logo.webp";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAuth } from "../../../context/AuthContext";

export default function Sidebar({
  expanded,
  onMobileClose,
  mobileOpen=false,
  expandedWidth=260,
  collapsedWidth=76
}){
  const navigate=useNavigate();
  const location=useLocation();
  const {colors,isDark}=useAppTheme();
  const {user}=useAuth();

  const isSuperAdmin=user?.rol==="SUPERADMIN";
  const canManage=["SUPERADMIN","ADMIN"].includes(user?.rol);

  const getInitialSection=()=>{
    if(
      location.pathname.startsWith("/admin/administrators")||
      location.pathname.startsWith("/admin/team-delegates")
    ){
      return "users";
    }

    if(
      location.pathname.startsWith("/admin/categories")||
      location.pathname.startsWith("/admin/teams")||
      location.pathname.startsWith("/admin/tournaments")
    ){
      return "competition";
    }

    if(
      location.pathname.startsWith("/admin/matches")||
      location.pathname.startsWith("/admin/venues")||
      location.pathname.startsWith("/admin/referees")||
      location.pathname.startsWith("/admin/transfer-market")
    ){
      return "matches";
    }

    return null;
  };

  const [openSection,setOpenSection]=useState(getInitialSection);

  const width=expanded?expandedWidth:collapsedWidth;

  const isHome=location.pathname==="/admin";

  const isAdministrators=
    location.pathname.startsWith("/admin/administrators");

  const isTeamDelegates=
    location.pathname.startsWith("/admin/team-delegates");

  const isUsersSectionActive=
    isAdministrators||
    isTeamDelegates;

  const isCategories=
    location.pathname.startsWith("/admin/categories");

  const isTeams=
    location.pathname.startsWith("/admin/teams");

  const isTournaments=
    location.pathname.startsWith("/admin/tournaments");

  const isCompetitionSectionActive=
    isCategories||
    isTeams||
    isTournaments;

  const isCalendarMatchesSelected=
    location.pathname.startsWith("/admin/matches")&&
    !location.pathname.startsWith("/admin/matches/live");

  const isVenues=
    location.pathname.startsWith("/admin/venues");

  const isReferees=
    location.pathname.startsWith("/admin/referees");

  const isTransferMarket=
    location.pathname.startsWith("/admin/transfer-market");

  const isMatchesSectionActive=
    location.pathname.startsWith("/admin/matches")||
    isVenues||
    isReferees||
    isTransferMarket;

  const isGallery=
    location.pathname.startsWith("/admin/gallery");

  const handleNavigate=(path)=>{
    navigate(path);
    if(onMobileClose) onMobileClose();
  };

  const handleSection=(section,collapsedPath)=>{
    if(!expanded){
      handleNavigate(collapsedPath);
      return;
    }

    setOpenSection(current=>current===section?null:section);
  };

  const activeColor=isDark?colors.text:colors.primary;

  const mainItemSx=(active=false)=>({
    borderRadius:"8px",
    mb:0.25,
    minHeight:38,
    px:expanded?1.2:0,
    justifyContent:expanded?"flex-start":"center",
    color:active?activeColor:colors.tableHeaderText,
    backgroundColor:active?colors.surface:"transparent",
    "& .MuiListItemIcon-root":{
      color:active?activeColor:colors.tableHeaderText,
    },
    "& .MuiListItemText-primary":{
      color:active?activeColor:colors.tableHeaderText,
    },
    "& .MuiSvgIcon-root":{
      color:active?activeColor:colors.tableHeaderText,
    },
    "&:hover":{
      backgroundColor:colors.surface,
      color:activeColor,
      "& .MuiListItemIcon-root":{
        color:activeColor
      },
      "& .MuiListItemText-primary":{
        color:activeColor
      },
      "& .MuiSvgIcon-root":{
        color:activeColor
      },
    },
  });

  const subItemSx=(selected=false)=>({
    ml:1.2,
    pl:2.3,
    minHeight:34,
    borderRadius:"7px",
    mb:0.15,
    color:selected?activeColor:colors.tableHeaderText,
    backgroundColor:selected?colors.surface:"transparent",
    "& .MuiListItemText-primary":{
      color:selected?activeColor:colors.tableHeaderText,
    },
    "&:hover":{
      backgroundColor:colors.surface,
      color:activeColor,
      "& .MuiListItemText-primary":{
        color:activeColor
      },
    },
    "&.Mui-selected":{
      backgroundColor:colors.surface,
      color:activeColor,
      "& .MuiListItemText-primary":{
        color:activeColor
      },
    },
    "&.Mui-selected:hover":{
      backgroundColor:colors.surface,
      color:activeColor,
      "& .MuiListItemText-primary":{
        color:activeColor
      },
    },
  });

  const mainIconSx={
    minWidth:expanded?34:0,
    justifyContent:"center",
  };

  const mainTextProps={
    fontSize:"0.84rem",
    fontWeight:600,
  };

  const subTextProps={
    fontSize:"0.80rem",
    fontWeight:500,
  };

  const content=(
    <Box
      sx={{
        height:"100%",
        display:"flex",
        flexDirection:"column",
        backgroundColor:colors.primary,
        overflow:"hidden"
      }}
    >
      <Box
        onClick={()=>handleNavigate("/admin")}
        sx={{
          height:72,
          px:expanded?1.5:1,
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          borderBottom:`1px solid ${colors.border}`,
          cursor:"pointer",
          flexShrink:0,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            display:"block",
            width:expanded?150:44,
            maxWidth:"100%",
            height:expanded?48:42,
            objectFit:"contain",
            borderRadius:"10px"
          }}
        />
      </Box>

      <Box
        sx={{
          flex:1,
          overflowY:"auto",
          overflowX:"hidden",
          px:0.8,
          py:1,
          "&::-webkit-scrollbar":{
            width:5
          },
          "&::-webkit-scrollbar-thumb":{
            backgroundColor:colors.borderDark,
            borderRadius:"10px"
          },
        }}
      >
        {expanded&&(
          <Typography
            sx={{
              px:1.2,
              mb:0.7,
              fontSize:"0.68rem",
              fontWeight:600,
              letterSpacing:"0.06em",
              color:colors.tableHeaderText,
              opacity:0.7
            }}
          >
            ADMINISTRACIÓN
          </Typography>
        )}

        <List disablePadding>
          <Tooltip title={expanded?"":"Inicio"} placement="right">
            <ListItemButton
              onClick={()=>handleNavigate("/admin")}
              sx={mainItemSx(isHome)}
            >
              <ListItemIcon sx={mainIconSx}>
                <HomeRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <ListItemText
                  primary="Inicio"
                  primaryTypographyProps={mainTextProps}
                />
              )}
            </ListItemButton>
          </Tooltip>

          {canManage&&(
            <>
              <Tooltip title={expanded?"":"Usuarios"} placement="right">
                <ListItemButton
                  onClick={()=>handleSection(
                    "users",
                    isSuperAdmin
                      ?"/admin/administrators"
                      :"/admin/team-delegates"
                  )}
                  sx={mainItemSx(isUsersSectionActive)}
                >
                  <ListItemIcon sx={mainIconSx}>
                    <ManageAccountsRoundedIcon sx={{fontSize:20}} />
                  </ListItemIcon>

                  {expanded&&(
                    <>
                      <ListItemText
                        primary="Usuarios"
                        primaryTypographyProps={mainTextProps}
                      />

                      {openSection==="users"
                        ?<ExpandLessRoundedIcon sx={{fontSize:18}} />
                        :<ExpandMoreRoundedIcon sx={{fontSize:18}} />
                      }
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              {expanded&&(
                <Collapse
                  in={openSection==="users"}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {isSuperAdmin&&(
                      <ListItemButton
                        onClick={()=>handleNavigate("/admin/administrators")}
                        selected={isAdministrators}
                        sx={subItemSx(isAdministrators)}
                      >
                        <ListItemText
                          primary="Administradores"
                          primaryTypographyProps={subTextProps}
                        />
                      </ListItemButton>
                    )}

                    <ListItemButton
                      onClick={()=>handleNavigate("/admin/team-delegates")}
                      selected={isTeamDelegates}
                      sx={subItemSx(isTeamDelegates)}
                    >
                      <ListItemText
                        primary="Delegados de equipos"
                        primaryTypographyProps={subTextProps}
                      />
                    </ListItemButton>
                  </List>
                </Collapse>
              )}
            </>
          )}

          <Tooltip title={expanded?"":"Competencia"} placement="right">
            <ListItemButton
              onClick={()=>handleSection(
                "competition",
                "/admin/categories"
              )}
              sx={mainItemSx(isCompetitionSectionActive)}
            >
              <ListItemIcon sx={mainIconSx}>
                <EmojiEventsRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <>
                  <ListItemText
                    primary="Competencia"
                    primaryTypographyProps={mainTextProps}
                  />

                  {openSection==="competition"
                    ?<ExpandLessRoundedIcon sx={{fontSize:18}} />
                    :<ExpandMoreRoundedIcon sx={{fontSize:18}} />
                  }
                </>
              )}
            </ListItemButton>
          </Tooltip>

          {expanded&&(
            <Collapse
              in={openSection==="competition"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <ListItemButton
                  onClick={()=>handleNavigate("/admin/categories")}
                  selected={isCategories}
                  sx={subItemSx(isCategories)}
                >
                  <ListItemText
                    primary="Categorías"
                    primaryTypographyProps={subTextProps}
                  />
                </ListItemButton>

                <ListItemButton
                  onClick={()=>handleNavigate("/admin/teams")}
                  selected={isTeams}
                  sx={subItemSx(isTeams)}
                >
                  <ListItemText
                    primary="Equipos"
                    primaryTypographyProps={subTextProps}
                  />
                </ListItemButton>

                {canManage&&(
                  <ListItemButton
                    onClick={()=>handleNavigate("/admin/tournaments")}
                    selected={isTournaments}
                    sx={subItemSx(isTournaments)}
                  >
                    <ListItemText
                      primary="Torneos"
                      primaryTypographyProps={subTextProps}
                    />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          )}

          {canManage&&(
            <>
              <Tooltip title={expanded?"":"Partidos"} placement="right">
                <ListItemButton
                  onClick={()=>handleSection(
                    "matches",
                    "/admin/matches"
                  )}
                  sx={mainItemSx(isMatchesSectionActive)}
                >
                  <ListItemIcon sx={mainIconSx}>
                    <CalendarMonthRoundedIcon sx={{fontSize:20}} />
                  </ListItemIcon>

                  {expanded&&(
                    <>
                      <ListItemText
                        primary="Partidos"
                        primaryTypographyProps={mainTextProps}
                      />

                      {openSection==="matches"
                        ?<ExpandLessRoundedIcon sx={{fontSize:18}} />
                        :<ExpandMoreRoundedIcon sx={{fontSize:18}} />
                      }
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              {expanded&&(
                <Collapse
                  in={openSection==="matches"}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <ListItemButton
                      onClick={()=>handleNavigate("/admin/matches")}
                      selected={isCalendarMatchesSelected}
                      sx={subItemSx(isCalendarMatchesSelected)}
                    >
                      <ListItemText
                        primary="Calendario de partidos"
                        primaryTypographyProps={subTextProps}
                      />
                    </ListItemButton>

                    <ListItemButton
                      onClick={()=>handleNavigate("/admin/venues")}
                      selected={isVenues}
                      sx={subItemSx(isVenues)}
                    >
                      <ListItemText
                        primary="Canchas"
                        primaryTypographyProps={subTextProps}
                      />
                    </ListItemButton>

                    <ListItemButton
                      onClick={()=>handleNavigate("/admin/referees")}
                      selected={isReferees}
                      sx={subItemSx(isReferees)}
                    >
                      <ListItemText
                        primary="Árbitros"
                        primaryTypographyProps={subTextProps}
                      />
                    </ListItemButton>

                    <ListItemButton
                      onClick={()=>handleNavigate("/admin/transfer-market")}
                      selected={isTransferMarket}
                      sx={subItemSx(isTransferMarket)}
                    >
                      <ListItemText
                        primary="Mercado de fichajes"
                        primaryTypographyProps={subTextProps}
                      />
                    </ListItemButton>
                  </List>
                </Collapse>
              )}
            </>
          )}

          <Tooltip title={expanded?"":"Galería"} placement="right">
            <ListItemButton
              onClick={()=>handleNavigate("/admin/gallery")}
              sx={mainItemSx(isGallery)}
            >
              <ListItemIcon sx={mainIconSx}>
                <PhotoLibraryRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <ListItemText
                  primary="Galería"
                  primaryTypographyProps={mainTextProps}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </List>
      </Box>

      <Box
        sx={{
          p:0.8,
          borderTop:`1px solid ${colors.border}`
        }}
      >
        <Tooltip
          title={expanded?"":"Ir al sitio web"}
          placement="right"
        >
          <ListItemButton
            onClick={()=>handleNavigate("/")}
            sx={mainItemSx(false)}
          >
            <ListItemIcon sx={mainIconSx}>
              <LanguageRoundedIcon sx={{fontSize:20}} />
            </ListItemIcon>

            {expanded&&(
              <ListItemText
                primary="Ir al sitio web"
                primaryTypographyProps={mainTextProps}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        open
        sx={{
          display:{
            xs:"none",
            md:"block"
          },
          width,
          flexShrink:0,
          "& .MuiDrawer-paper":{
            width,
            boxSizing:"border-box",
            border:0,
            transition:"width .22s ease",
            overflow:"hidden",
          },
        }}
      >
        {content}
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted:true
        }}
        sx={{
          display:{
            xs:"block",
            md:"none"
          },
          "& .MuiDrawer-paper":{
            width:expandedWidth,
            boxSizing:"border-box",
            border:0,
          },
        }}
      >
        {content}
      </Drawer>
    </>
  );
}